import { sleep } from './sleep';
import { addJitterToTime } from './addJitterToTime';

export type RetryStrategy = (
  retries: number,
  error: Error,
) => number | Promise<void> | undefined;

export const createConstantRetryStrategy =
  ({
    wait,
    maxRetries,
    shouldRetryError,
  }: {
    wait: number;
    maxRetries?: number;
    shouldRetryError?(err: unknown): boolean;
  }): RetryStrategy =>
  (retries, error) => {
    if (error && shouldRetryError && !shouldRetryError(error)) {
      return undefined;
    }

    if (maxRetries !== undefined && retries > maxRetries) {
      return undefined;
    }

    return wait;
  };

export const createLinearRetryStrategy =
  ({
    min,
    max,
    step = min,
    maxRetries,
    shouldRetryError,
  }: {
    min: number;
    max: number;
    step?: number;
    maxRetries?: number;
    shouldRetryError?(err: unknown): boolean;
  }): RetryStrategy =>
  (retries, error) => {
    if (error && shouldRetryError && !shouldRetryError(error)) {
      return undefined;
    }

    if (maxRetries !== undefined && retries > maxRetries) {
      return undefined;
    }

    const delay = min + step * retries;
    return Math.min(max, delay);
  };

export const createExponentialRetryStrategy =
  ({
    step,
    max,
    multiplier = 2,
    maxRetries,
    shouldRetryError,
  }: {
    max: number;
    multiplier: number;
    step: number;
    maxRetries?: number;
    shouldRetryError?(err: unknown): boolean;
  }): RetryStrategy =>
  (retries, error) => {
    if (error && shouldRetryError && !shouldRetryError(error)) {
      return undefined;
    }

    if (maxRetries !== undefined && retries > maxRetries) {
      return undefined;
    }

    const delay = step * multiplier ** retries;
    return Math.min(max, delay);
  };

export const withRetries = async <R>(
  fn: () => R | Promise<R>,
  retryStrategy: RetryStrategy | undefined | null,

  showMessages = true,
): Promise<R> => {
  let retries = 0;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    try {
      // eslint-disable-next-line no-await-in-loop
      return await fn();
    } catch (e) {
      const err = e as Error;
      const delay = retryStrategy?.(retries, err);

      if (delay == null) {
        throw e;
      }

      retries += 1;

      if (delay instanceof Promise) {
        if (showMessages) {
          // eslint-disable-next-line no-console
          console.warn(`there was an error (will retry later):`, err?.message ?? err);
        }

        // eslint-disable-next-line no-await-in-loop
        await delay;
      } else {
        if (showMessages) {
          // eslint-disable-next-line no-console
          console.warn(
            `there was an error (will retry in ${delay} msecs):`,
            err?.message ?? err,
          );
        }

        // eslint-disable-next-line no-await-in-loop
        await sleep(delay);
      }
    }
  }
};

export function addJitter(
  retryStrategy: RetryStrategy,
  options: {
    min: number;
    max: number;
  },
): RetryStrategy {
  if (options.min < 0) {
    throw new Error('min jitter cannot be negative');
  }

  if (options.max < options.min) {
    throw new Error('max jitter cannot be less than min jitter');
  }

  return (retries, error) => {
    const delay = retryStrategy(retries, error);

    if (delay instanceof Promise) {
      throw new Error(
        'addJitter cannot be used with retry strategies that return a promise',
      );
    }

    if (delay === undefined || delay <= 0) {
      return delay;
    }

    return addJitterToTime(delay, options);
  };
}
